'use client';

import {
  POPULARITY_CONTEXT_KEY,
  POPULARITY_SORT_VALUE,
  RATING_CONTEXT_KEY,
  RATING_SORT_VALUE,
  getSortValueForRankingContext,
} from '@lib/coveo/sortCriteriaData';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchSurface, useSurfaceHub } from '../SearchSurfaceContext';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import {
  addBooleanContext,
  type ContextMethods,
} from '../headless/rankingContext';
import {
  POPULARITY_SORT,
  RELEVANCE_SORT,
  RELEVANCY_CRITERION_EXPRESSION,
  criterionToExpression,
  getSortCriteriaForSearchHub,
} from '../headless/sortCriteria';

/**
 * Writes both ranking-mode flags on every call so the two can never drift —
 * the same invariant as `applyRankingContext` in
 * `@lib/coveo/sortCriteria`.
 */
function applyRankingContext(
  contextMethods: ContextMethods,
  sortValue: string
) {
  addBooleanContext(
    contextMethods,
    POPULARITY_CONTEXT_KEY,
    sortValue === POPULARITY_SORT_VALUE
  );
  addBooleanContext(
    contextMethods,
    RATING_CONTEXT_KEY,
    sortValue === RATING_SORT_VALUE
  );
}

/**
 * Sort state for SearchV2, on the generated `useSort` + `useContext` hooks.
 *
 * The surface (search vs listing) comes from SearchSurfaceContext, which the
 * page seeds server-side, rather than from the engine's configured searchHub
 * (not readable off the generated hooks): listing pages default to popularity,
 * search pages to relevance.
 *
 * `currentSortValue` is DERIVED from engine state (the sort expression, then
 * the ranking-context flags for the relevancy-based modes), never mirrored
 * into local state, so that:
 * - the server render of a deep link with `sortCriteria` in the URL already
 *   shows the right label (the static state carries the seeded expression);
 * - browser back/forward to the relevancy default resyncs the label (a local
 *   mirror has no state change to react to when the expression returns to
 *   'relevancy');
 * - a pre-hydration selection (methods still undefined) cannot leave the
 *   trigger showing a sort that was never applied — and `isReady` lets the
 *   control disable itself for that window (which persists if hydration
 *   fails and the static controllers stay active), so a selection is refused
 *   visibly instead of silently dropped;
 * - every mounted sort control agrees, once Task 08 adds the mobile drawer.
 *
 * The `popularity` context is SEEDED server-side for listing and carried
 * through hydration alongside the other context values, so this hook only
 * UPDATES the ranking flags in response to interaction (handleSort) or a
 * URL-restored field sort — it does not re-apply the initial default on mount.
 */
export const useSearchV2Sort = () => {
  const { isListing } = useSearchSurface();
  const hub = useSurfaceHub();
  const defaultSort = isListing ? POPULARITY_SORT : RELEVANCE_SORT;
  const sortCriteria = getSortCriteriaForSearchHub(hub);

  const controllers = useSearchControllers();
  const { state: sortState, methods: sortMethods } = controllers.useSort();
  const { state: contextState, methods: contextMethods } =
    controllers.useContext();

  // Falls back to defaultSort for unknown criteria (stale/hand-edited URLs)
  // so the ranking context never drifts out of sync with the displayed sort.
  const fieldSortValue = useMemo(() => {
    if (sortState.sortCriteria === RELEVANCY_CRITERION_EXPRESSION) return null;

    const matched = sortCriteria.find(
      (opt) => criterionToExpression(opt.criterion) === sortState.sortCriteria
    );
    return matched?.value ?? defaultSort.value;
  }, [sortState.sortCriteria, sortCriteria, defaultSort.value]);

  // Clear the ranking flags when the URL restores a field sort, which the
  // SearchParameterManager applies straight to the engine without going
  // through handleSort.
  useEffect(() => {
    if (fieldSortValue) {
      applyRankingContext(contextMethods, fieldSortValue);
    }
  }, [fieldSortValue, contextMethods]);

  const currentSortValue =
    fieldSortValue ??
    getSortValueForRankingContext(contextState.values, defaultSort.value);

  const handleSort = useCallback(
    (value: string) => {
      const option = sortCriteria.find((opt) => opt.value === value);
      if (!option || !sortMethods) return;
      applyRankingContext(contextMethods, value);
      sortMethods.sortBy(option.criterion);
    },
    [sortMethods, contextMethods, sortCriteria]
  );

  return {
    currentSortValue,
    sortCriteria,
    handleSort,
    isReady: !!sortMethods,
  };
};
